import { DomainHit, FilterState, RunMetadata } from './types';

const STORAGE_KEY = 'growgeo_v1_state';

export interface StorageState {
  domainMap: Array<{
    domain: string;
    usedBy: string[];
    consideredBy: string[];
    usedUrls: string[];
    consideredUrls: string[];
    providers: string[];
    providerBreakdown: any;
    favicon: string;
    hasAccess?: boolean;
    tag?: string;
  }>;
  metadata: RunMetadata;
  filters: Omit<FilterState, 'providers'> & { providers: string[] };
  timestamp: string;
}

export const saveToLocalStorage = (
  domainMap: Map<string, DomainHit>,
  metadata: RunMetadata,
  filters: FilterState
): void => {
  try {
    const serialized: StorageState = {
      domainMap: Array.from(domainMap.values()).map((hit) => ({
        domain: hit.domain,
        usedBy: Array.from(hit.usedBy),
        consideredBy: Array.from(hit.consideredBy),
        usedUrls: hit.usedUrls,
        consideredUrls: hit.consideredUrls,
        providers: Array.from(hit.providers),
        providerBreakdown: hit.providerBreakdown,
        favicon: hit.favicon,
        hasAccess: hit.hasAccess,
        tag: hit.tag,
      })),
      metadata,
      filters: {
        ...filters,
        providers: Array.from(filters.providers),
      },
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

export const loadFromLocalStorage = (): {
  domainMap: Map<string, DomainHit>;
  metadata: RunMetadata;
  filters: FilterState;
} | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed: StorageState = JSON.parse(stored);

    const domainMap = new Map<string, DomainHit>();
    parsed.domainMap.forEach((item) => {
      domainMap.set(item.domain, {
        domain: item.domain,
        usedBy: new Set(Array.isArray(item.usedBy) ? item.usedBy : []),
        consideredBy: new Set(Array.isArray(item.consideredBy) ? item.consideredBy : []),
        usedUrls: item.usedUrls || [],
        consideredUrls: item.consideredUrls || [],
        providers: new Set(Array.isArray(item.providers) ? item.providers as any : []),
        providerBreakdown: item.providerBreakdown || {},
        favicon: item.favicon,
        hasAccess: item.hasAccess,
        tag: item.tag as any,
      });
    });

    return {
      domainMap,
      metadata: parsed.metadata,
      filters: {
        ...parsed.filters,
        providers: new Set(parsed.filters.providers as any),
      },
    };
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    return null;
  }
};

export const clearLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
  }
};
